import apiClient from './apiClient';
import { ABHA_SERVICE_ERROR } from '../../constants/abdmCopy';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ABHA API — ABDM Milestone 1 endpoints
 *
 * Design rules enforced here:
 *   1. Every function returns a normalized domain-shaped object.
 *      Components never see axios response envelopes or raw backend keys.
 *   2. Errors carry declarative metadata (retryable, allowSkip) so UI
 *      layers become conditional on flags, not string-matched messages.
 *   3. X-Correlation-Id is injected by the apiClient interceptor, not here.
 *   4. No URL construction outside this file.
 *
 * Base: /api/organisation/abha/
 *
 * Creation flow:
 *   requestCreationOTP  → verifyCreationOTP → getAddressSuggestions
 *   → setABHAAddress → downloadABHACard
 *
 * Verification flow (existing ABHA holders):
 *   requestVerificationOTP → confirmVerificationOTP
 *
 * Patient linking + facility:
 *   lookupPatientByABHA · linkABHAToPatient · checkABHAHealth
 * ═══════════════════════════════════════════════════════════════════════
 */

const BASE = '/api/organisation/abha';

// Bump when the consent wording the user acknowledges in ABHACreationFlow changes.
export const ABHA_CONSENT_VERSION = 'abha-create-v1';

// ── Error handling ────────────────────────────────────────────────────

/**
 * ABHA error code classification.
 * UI layers switch on `code` and `retryable`/`allowSkip` flags —
 * never on raw message strings.
 *
 * retryable  — the same action can be retried without user change
 * allowSkip  — ABHA flow can be abandoned; patient reg must still proceed
 */
const _ERROR_CODES = {
  // 4xx — user errors, not retryable
  INVALID_AADHAAR:    { retryable: false, allowSkip: false },
  OTP_EXPIRED:        { retryable: false, allowSkip: false },
  OTP_INVALID:        { retryable: false, allowSkip: false },
  TXN_EXPIRED:        { retryable: false, allowSkip: false },
  DUPLICATE_ABHA:     { retryable: false, allowSkip: false },
  ABHA_NOT_FOUND:     { retryable: false, allowSkip: false },
  CONSENT_REQUIRED:   { retryable: false, allowSkip: false },
  CONFLICT:           { retryable: false, allowSkip: false },
  // HIL (M2 HIP-Initiated Linking) — structured 422, see _buildError below
  DEMOGRAPHICS_INCOMPLETE: { retryable: false, allowSkip: true },
  // 429 — rate limited
  RATE_LIMITED:       { retryable: false, allowSkip: true },
  // 5xx / network — retryable; ABHA degradation is always allowed
  SERVICE_UNAVAILABLE:{ retryable: true,  allowSkip: true },
  NETWORK_ERROR:      { retryable: true,  allowSkip: true },
  UNKNOWN:            { retryable: false, allowSkip: true },
};

function _buildError(error) {
  const status = error?.response?.status;
  const rawDetail = error?.response?.data?.detail;
  // rawDetail can be a structured object (e.g. HIL's
  // {error: 'demographics_incomplete', missing_fields: [...]}) as well as
  // a plain string — never call string methods on it directly.
  // New contract (§2.11): detail = {code, message, retry_after?, field?}. Its `message` is the
  // user-facing text; its `code` is exposed as err.abhaCode (err.code stays the coarse class below).
  const structured = rawDetail && typeof rawDetail === 'object' && typeof rawDetail.code === 'string' && typeof rawDetail.message === 'string'
    ? rawDetail : null;
  const detail = typeof rawDetail === 'string'
    ? rawDetail
    : (structured?.message || error?.response?.data?.message || '');

  let code = 'UNKNOWN';
  if (!status) {
    code = 'NETWORK_ERROR';
  } else if (status === 429) {
    code = 'RATE_LIMITED';
  } else if (status >= 500) {
    code = 'SERVICE_UNAVAILABLE';
  } else if (status === 409) {
    code = 'CONFLICT';
  } else if (rawDetail && typeof rawDetail === 'object' && rawDetail.error === 'demographics_incomplete') {
    code = 'DEMOGRAPHICS_INCOMPLETE';
  } else if (status >= 400 && !structured) {
    // Map backend user-message keywords to a stable code
    const msg = detail.toLowerCase();
    if (msg.includes('aadhaar'))       code = 'INVALID_AADHAAR';
    else if (msg.includes('otp') && msg.includes('expir')) code = 'OTP_EXPIRED';
    else if (msg.includes('invalid otp'))                  code = 'OTP_INVALID';
    else if (msg.includes('session expir'))                code = 'TXN_EXPIRED';
    else if (msg.includes('already exists'))               code = 'DUPLICATE_ABHA';
    else if (msg.includes('no abha') || msg.includes('not found')) code = 'ABHA_NOT_FOUND';
    else if (msg.includes('consent'))                      code = 'CONSENT_REQUIRED';
  }

  const meta = _ERROR_CODES[code] ?? _ERROR_CODES.UNKNOWN;
  const missingFields = code === 'DEMOGRAPHICS_INCOMPLETE' ? (rawDetail.missing_fields || []) : undefined;
  const message = code === 'DEMOGRAPHICS_INCOMPLETE'
    ? `Missing patient details: ${missingFields.join(', ')}`
    : (detail || 'ABHA request failed');

  const err = new Error(message);
  err.code = code;
  // Structured 409 from the link endpoint: { code: 'DOB_MISMATCH' | ..., tries_left }.
  if (rawDetail && typeof rawDetail === 'object' && typeof rawDetail.code === 'string') {
    err.linkCode = rawDetail.code;
    if (rawDetail.tries_left != null) err.triesLeft = rawDetail.tries_left;
  }
  if (structured) {
    err.abhaCode = structured.code;
    if (structured.retry_after != null) err.retryAfter = structured.retry_after;
    if (structured.field) err.field = structured.field;
  }
  err.status = status;
  err.retryable = meta.retryable;
  err.allowSkip = meta.allowSkip;
  err.userMessage = message || ABHA_SERVICE_ERROR;
  if (missingFields) err.missingFields = missingFields;
  return err;
}


// ── Captcha before an OTP first send (NHA VRFY_ABHA_301) ─────────────

export { CAPTCHA_ERROR_CODES, isCaptchaError } from '../../utils/abhaCaptcha';

// Only verify by communication mobile has a captcha (NHA VRFY_ABHA_301; owner: only as per the M1 sheet).
const _CAPTCHA_PATHS = { verify: 'verify' };

/**
 * Get a captcha for the OTP send it precedes. The server binds it to `fields` (the same values the
 * OTP request then sends) and to the logged-in user, so it only unlocks that one send.
 *
 * @param {'verify'} purpose
 * @param {object} fields  {login_hint: 'mobile', login_id, otp_method?}
 * @returns {{ enabled: false } | { enabled: true, captchaId, imagePngBase64, expiresIn }}
 */
export async function issueAbhaCaptcha(purpose, fields) {
  const path = _CAPTCHA_PATHS[purpose];
  if (!path) throw new Error(`unknown captcha purpose: ${purpose}`);
  try {
    const { data } = await apiClient.post(`${BASE}/captcha/${path}`, fields);
    if (!data?.enabled) return { enabled: false };
    return {
      enabled:        true,
      captchaId:      data.captcha_id,
      imagePngBase64: data.image_png_base64,
      expiresIn:      data.expires_in,
    };
  } catch (err) {
    throw _buildError(err);
  }
}

// Captcha answer on a FIRST send only; resends never carry one (the server decides from its own state).
const _captchaFields = (captcha) => (captcha
  ? { captcha_id: captcha.captchaId, captcha_text: captcha.captchaText }
  : {});

// OTP-sent response (§2.7/2.9/2.10): masked mobile + how many resends remain.
function _otpSent(data) {
  return {
    txnId:        data.txn_id,
    message:      data.message,
    maskedMobile: data.masked_mobile ?? null,
    resendsLeft:  data.resends_left,
  };
}

// ── ABHA Creation — 5 steps ───────────────────────────────────────────

/**
 * Step 1 — Send Aadhaar OTP.
 *
 * @param {{ aadhaarNumber: string, consentGiven: boolean }} params
 * @returns {{ txnId: string, message: string }}
 */
export async function requestCreationOTP({ aadhaarNumber, consentGiven, consent, resend = false }) {
  try {
    const { data } = await apiClient.post(`${BASE}/create/request-otp`, {
      aadhaar_number: aadhaarNumber,
      consent_given:  consentGiven,
      resend,
      // What was actually acknowledged. The server validates every part and stamps its own
      // timestamp + the authenticated member; the client never supplies who/when.
      ...(consent && {
        consent: {
          version:              ABHA_CONSENT_VERSION,
          declaration_accepted: consent.declarationAccepted,
          worker_consent:       consent.workerConsent,
          beneficiary_consent:  consent.beneficiaryConsent,
          beneficiary_name:     String(consent.beneficiaryName || '').trim(),
          // R80: each statement stored separately.
          acknowledgements:     consent.acknowledgements,
        },
      }),
    });
    return _otpSent(data);
  } catch (err) {
    throw _buildError(err);
  }
}

/**
 * Step 2 — Verify OTP and create ABHA number.
 *
 * @param {{ txnId: string, otp: string, mobile: string }} params
 * @returns {{ txnId, abhaNumber, abhaAddress, name, gender, yearOfBirth, mobile }}
 */
export async function verifyCreationOTP({ txnId, otp, mobile }) {
  try {
    const { data } = await apiClient.post(`${BASE}/create/verify-otp`, {
      txn_id: txnId,
      otp,
      mobile,
    });
    return {
      txnId:       data.txn_id,
      abhaNumber:  data.abha_number,
      abhaAddress: data.abha_address,
      name:        data.name,
      gender:      data.gender,
      yearOfBirth: data.year_of_birth,
      dob:         data.dob || '',
      mobile:      data.mobile,
      isExisting:  data.is_existing ?? false,
      middleName:      data.middle_name || '',
      email:           data.email || '',
      addressLine:     data.address_line || '',
      stateName:       data.state_name || '',
      districtName:    data.district_name || '',
      subdistrictName: data.subdistrict_name || '',
      townName:        data.town_name || '',
      pincode:         data.pincode || '',
      mobileVerificationRequired: Boolean(data.mobile_verification_required),
      // Existing ABHA: all its ABHA addresses, preferred first (staff pick which this clinic uses).
      abhaAddresses: Array.isArray(data.abha_addresses) ? data.abha_addresses : [],
    };
  } catch (err) {
    throw _buildError(err);
  }
}

/** Communication-mobile OTP (§2.10) — send (or resend) an OTP to the mobile to be kept on the ABHA. */
export async function requestCreationMobileOTP({ txnId, mobile, resend = false }) {
  try {
    const { data } = await apiClient.post(`${BASE}/create/mobile/request-otp`, {
      txn_id: txnId, mobile, resend,
    });
    return _otpSent(data);
  } catch (err) {
    throw _buildError(err);
  }
}

/** Communication-mobile OTP (§2.10) — verify it. */
export async function verifyCreationMobileOTP({ txnId, otp }) {
  try {
    const { data } = await apiClient.post(`${BASE}/create/mobile/verify-otp`, {
      txn_id: txnId, otp,
    });
    return { txnId: data.txn_id, mobileVerified: Boolean(data.mobile_verified) };
  } catch (err) {
    throw _buildError(err);
  }
}

/**
 * Step 3a — Fetch ABHA address suggestions.
 *
 * @param {{ txnId: string }} params
 * @returns {{ txnId: string, suggestions: string[] }}
 */
export async function getAddressSuggestions({ txnId }) {
  try {
    const { data } = await apiClient.post(`${BASE}/create/suggestions`, {
      txn_id: txnId,
    });
    return {
      txnId:       data.txn_id,
      suggestions: data.suggestions ?? [],
      abhaAddressSuffix: data.abha_address_suffix ?? null,
    };
  } catch (err) {
    throw _buildError(err);
  }
}

/**
 * Step 3b — Confirm chosen ABHA address.
 *
 * @param {{ txnId: string, abhaAddress: string }} params
 * @returns {{ txnId: string, abhaAddress: string }}
 */
export async function setABHAAddress({ txnId, abhaAddress }) {
  try {
    const { data } = await apiClient.post(`${BASE}/create/set-address`, {
      txn_id:       txnId,
      abha_address: abhaAddress,
    });
    return { txnId: data.txn_id, abhaAddress: data.abha_address };
  } catch (err) {
    throw _buildError(err);
  }
}

/**
 * Step 4 — Download ABHA card PNG.
 *
 * Returns an object-URL string pointing to the PNG blob.
 * Caller is responsible for revoking via URL.revokeObjectURL() when done.
 *
 * @param {{ txnId: string }} params
 * @returns {{ objectUrl: string, contentType: string }}
 */
/** The ABHA card already stored for this org (Create or Verify flow) as an object URL. */
export async function getStoredABHACard(abhaNumber) {
  try {
    const response = await apiClient.get(`${BASE}/card/stored`, {
      params:       { abha_number: abhaNumber },
      responseType: 'blob',
    });
    const blob      = response.data;
    const objectUrl = URL.createObjectURL(blob);
    return { objectUrl, contentType: blob.type || 'image/png' };
  } catch (err) {
    throw _buildError(err);
  }
}

export async function downloadABHACard({ txnId }) {
  try {
    const response = await apiClient.get(`${BASE}/create/card`, {
      params:       { txn_id: txnId },
      responseType: 'blob',
    });
    const blob      = response.data;
    const objectUrl = URL.createObjectURL(blob);
    return { objectUrl, contentType: blob.type || 'image/png' };
  } catch (err) {
    // R32: a blob request delivers the error body as a Blob — parse it so the real reason survives.
    const body = err?.response?.data;
    if (typeof Blob !== 'undefined' && body instanceof Blob) {
      try { err.response.data = JSON.parse(await body.text()); } catch (_) { /* keep blob */ }
    }
    throw _buildError(err);
  }
}

/**
 * ABHA card for the just-verified live session (§2.12, R29) — NOT the stored card.
 * Errors on a blob request arrive as a Blob, so the JSON body is parsed before mapping:
 * 404 → err.abhaCode 'CARD_NOT_AVAILABLE'.
 */
export async function downloadVerifiedABHACard({ txnId }) {
  try {
    const response = await apiClient.get(`${BASE}/verify/card`, {
      params:       { txn_id: txnId },
      responseType: 'blob',
    });
    const blob      = response.data;
    const objectUrl = URL.createObjectURL(blob);
    return { objectUrl, contentType: blob.type || 'image/png' };
  } catch (err) {
    const body = err?.response?.data;
    if (typeof Blob !== 'undefined' && body instanceof Blob) {
      try { err.response.data = JSON.parse(await body.text()); } catch (_) { /* keep blob */ }
    }
    throw _buildError(err);
  }
}


// ── ABHA Verification — 2 or 3 steps ─────────────────────────────────
// abha-number / aadhaar: 2 steps (request OTP → confirm OTP)
// mobile:                3 steps (request OTP → confirm OTP → select ABHA)

/**
 * Step 1 — Send OTP via the chosen login method.
 *
 * @param {{ loginHint?: 'mobile'|'aadhaar'|'abha-number'|'abha-address', loginId: string,
 *           otpMethod?: 'mobile'|'aadhaar', resend?: boolean,
 *           captcha?: { captchaId: string, captchaText: string } }} params
 *   otpMethod is only used for abha-number / abha-address; the server defaults it to 'mobile'.
 * @returns {{ txnId, message, maskedMobile: string|null, resendsLeft: number }}
 */
export async function requestVerificationOTP({ loginHint = 'mobile', loginId, otpMethod, resend = false, captcha }) {
  try {
    const { data } = await apiClient.post(`${BASE}/verify/request-otp`, {
      login_hint: loginHint,
      login_id:   loginId,
      ...(otpMethod ? { otp_method: otpMethod } : {}),
      resend,
      ..._captchaFields(captcha),
    });
    return _otpSent(data);
  } catch (err) {
    throw _buildError(err);
  }
}

/**
 * Step 2 — Verify OTP.
 * For abha-number/aadhaar: returns full profile.
 * For mobile: returns mappedAbhaList (non-empty → call selectVerificationABHA).
 *
 * @param {{ txnId: string, otp: string, abhaNumber?: string }} params
 * @returns {{ txnId, abhaNumber, abhaAddress, name, gender, yearOfBirth, mobile, mappedAbhaList }}
 */
export async function confirmVerificationOTP({ txnId, otp, abhaNumber = '' }) {
  try {
    const { data } = await apiClient.post(`${BASE}/verify/confirm`, {
      txn_id:      txnId,
      otp,
      abha_number: abhaNumber,
    });
    return {
      txnId:          data.txn_id,
      abhaNumber:     data.abha_number,
      abhaAddress:    data.abha_address,
      name:           data.name,
      gender:         data.gender,
      yearOfBirth:    data.year_of_birth,
      mobile:         data.mobile,
      mappedAbhaList: data.mapped_abha_list ?? [],
      ..._verifyProfileExtras(data),
    };
  } catch (err) {
    throw _buildError(err);
  }
}

// Profile fields on the verify responses (absent on older backends -> undefined, shown as "—").
const _verifyProfileExtras = (data) => ({
  dob:          data.date_of_birth ?? data.dob,
  profilePhoto: data.profile_photo,
  addressLine:  data.address_line ?? data.address,
  stateName:    data.state_name,
  districtName: data.district_name,
});

/**
 * Step 3 (mobile flow only) — Select one ABHA from mappedAbhaList.
 *
 * @param {{ txnId: string, abhaNumber: string }} params
 * @returns {{ txnId, abhaNumber, abhaAddress, name, gender, yearOfBirth, mobile }}
 */
export async function selectVerificationABHA({ txnId, abhaNumber }) {
  try {
    const { data } = await apiClient.post(`${BASE}/verify/select-abha`, {
      txn_id:      txnId,
      abha_number: abhaNumber,
    });
    return {
      txnId:       data.txn_id,
      abhaNumber:  data.abha_number,
      abhaAddress: data.abha_address,
      name:        data.name,
      gender:      data.gender,
      yearOfBirth: data.year_of_birth,
      mobile:      data.mobile,
      ..._verifyProfileExtras(data),
    };
  } catch (err) {
    throw _buildError(err);
  }
}


// ── Patient linking ───────────────────────────────────────────────────

/**
 * Check whether an ABHA number is already linked to a patient in this org.
 *
 * @param {{ abhaNumber: string }} params
 * @returns {{ found: boolean, patientId?: string, patientName?: string }}
 */
export async function lookupPatientByABHA({ abhaNumber }) {
  try {
    const { data } = await apiClient.get(`${BASE}/patient`, {
      params: { abha_number: abhaNumber },
    });
    return {
      found:       data.found,
      patientId:   data.patient_id   ?? null,
      patientName: data.patient_name ?? null,
    };
  } catch (err) {
    throw _buildError(err);
  }
}

/**
 * Persist ABHA data to an existing patient record.
 * Uses optimistic concurrency — `recordVersion` must match the patient's
 * current `record_version` from the most recent PatientResponse.
 * (Scan & Share links go through claimDiscoveredScan by scan_id, not here.)
 *
 * @param {{ patientId: string, abhaNumber: string, abhaAddress: string, recordVersion: number }} params
 * @returns {{ status: string, patientId: string }}
 */
export async function linkABHAToPatient({ patientId, abhaNumber, abhaAddress, recordVersion, profile, statedDob }) {
  try {
    const { data } = await apiClient.post(`${BASE}/link/${patientId}`, {
      abha_number:    abhaNumber,
      abha_address:   abhaAddress,
      record_version: recordVersion,
      ...(statedDob ? { stated_dob: statedDob } : {}),   // YYYY-MM-DD, asked of the patient when our record has none
      ...(profile?.consentWorkerName ? { consent_worker_name: profile.consentWorkerName } : {}),
      ...(profile?.consentBeneficiaryName ? { consent_beneficiary_name: profile.consentBeneficiaryName } : {}),
      // Full verified profile (snake_case for the backend) — captured as an ABDM snapshot +
      // used to enrich patient demographics. Absent/blank fields are ignored server-side.
      ...(profile ? {
        name:              profile.fullName || '',
        first_name:        profile.firstName || '',
        middle_name:       profile.middleName || '',
        last_name:         profile.lastName || '',
        gender:            profile.gender || '',
        year_of_birth:     profile.yearOfBirth || '',
        dob:               profile.dob || '',
        mobile:            profile.mobile || '',
        email:             profile.email || '',
        address_line:      profile.addressLine || '',
        state_name:        profile.stateName || '',
        district_name:     profile.districtName || '',
        subdistrict_name:  profile.subdistrictName || '',
        town_name:         profile.townName || '',
        pincode:           profile.pincode || '',
        verification_type: profile.verificationType || '',
        ...(profile.kycVerified ? { kyc_verified: true } : {}),
      } : {}),
    });
    return {
      status: data.status,
      patientId: data.patient_id,
      dobSaved: Boolean(data.dob_saved),
      nameUpdated: Boolean(data.name_updated),
    };
  } catch (err) {
    throw _buildError(err);
  }
}

/**
 * "Is this the same person?" preview — read-only comparison of the patient record with the
 * verified ABHA, used BEFORE linking to an EXISTING patient. The ABHA date of birth is only
 * present when it matches ours exactly (dobStatus MATCH).
 */
export async function getAbhaLinkPreview({ patientId, abhaNumber }) {
  try {
    const { data } = await apiClient.get(`${BASE}/link-preview/${patientId}`, {
      params: { abha_number: abhaNumber },
    });
    return {
      record: data.record,
      abha: data.abha,
      dobStatus: data.dob_status,
      nameDiffers: Boolean(data.name_differs),
      genderMatches: data.gender_matches ?? null,   // null = record has no gender (neutral)
      triesLeft: data.tries_left ?? 0,
      alreadyLinkedElsewhere: Boolean(data.already_linked_elsewhere),
    };
  } catch (err) {
    throw _buildError(err);
  }
}


// ── Scan & Share ──────────────────────────────────────────────────────

/**
 * Desk-wide Scan & Share waiting list (R56): every unclaimed, unexpired scan for the org, newest
 * first (max 50). Masked evidence only. ADMIN / DOCTOR / RECEPTIONIST; other roles get 403.
 *
 * @returns {{ entries: Array<{ tokenNumber, scanId, nameMasked, gender, yearOfBirth, mobileMasked, scannedAt, expiresAt }>, serverTime }}
 */
export async function getScanQueue() {
  try {
    const { data } = await apiClient.get(`${BASE}/scan-share/queue`);
    return {
      entries: (data.entries || []).map((e) => ({
        tokenNumber:  e.token_number ?? null,
        scanId:       e.scan_id,
        nameMasked:   e.name_masked ?? '',
        gender:       e.gender ?? '',
        yearOfBirth:  e.age_or_year_of_birth ?? '',
        mobileMasked: e.mobile_masked ?? '',
        abhaMasked:   e.abha_address_masked || e.abha_number_masked || '',
        scannedAt:    e.scanned_at,
        expiresAt:    e.expires_at,
        counterId:    e.counter_id || '',
        counterName:  e.counter_name ?? null,
        counterType:  e.counter_type ?? null,
      })),
      serverTime: data.server_time ?? null,
    };
  } catch (err) {
    throw _buildError(err);
  }
}

/**
 * Reveal one waiting scan's UNMASKED demographics — called only on an explicit staff pick (= the
 * human match). Returns null when the scan is gone / already claimed (server answers {}).
 *
 * @param {{ scanId: string }} params
 * @returns {{ name, gender, yearOfBirth, mobile } | null}
 */
export async function revealPendingScan({ scanId }) {
  try {
    const { data } = await apiClient.get(`${BASE}/scan-share/pending/reveal`, {
      params: { scan_id: scanId },
    });
    if (!data || !(data.name || data.gender || data.year_of_birth)) return null;
    return {
      name:        data.name          ?? '',
      gender:      data.gender        ?? '',
      yearOfBirth: data.year_of_birth ?? '',
      mobile:      data.mobile        ?? '',
    };
  } catch (err) {
    throw _buildError(err);
  }
}

/**
 * Claim one picked scan for a registered/selected patient (claim→link→consume server-side).
 * 200 CLAIMED | ALREADY_CLAIMED_BY_SELF; failures carry err.abhaCode: NO_PENDING_SCAN (404),
 * SCAN_ALREADY_CLAIMED | ABHA_ALREADY_LINKED | SCAN_SHARE_EXISTING_PATIENT (409), SCAN_GONE (410).
 *
 * @param {{ patientId: string, scanId: string }} params
 * @returns {{ status: string, patientId: string, sameAbha: boolean }}  sameAbha = attached without OTP (scan equals the patient's verified ABHA)
 */
export async function claimDiscoveredScan({ patientId, scanId }) {
  try {
    const { data } = await apiClient.post(`${BASE}/scan-share/claim`, {
      patient_id:     patientId,
      scan_id:        scanId,
      record_version: 0,
    });
    return { status: data.status, patientId: data.patient_id, sameAbha: Boolean(data.same_abha) };
  } catch (err) {
    const e = _buildError(err);
    // 404/410 bodies may carry only {code}; keep the contract code readable as abhaCode.
    if (!e.abhaCode && e.linkCode) e.abhaCode = e.linkCode;
    throw e;
  }
}


// ── ABHA record (returning patients) ─────────────────────────────────

/**
 * Fetch the ABHA record for a patient already in this org.
 * Called by ABHASection when abha_status !== 'NOT_LINKED'.
 *
 * @param {{ patientId: string }} params
 * @returns {{ hasAbha, abhaStatus, abhaNumberMasked, abhaAddress, verifiedAt, detailsAvailable }}
 */
export async function getABHARecord({ patientId }) {
  try {
    const { data } = await apiClient.get(`${BASE}/record/${patientId}`);
    return {
      hasAbha:          data.has_abha,
      abhaStatus:       data.abha_status,
      abhaNumberMasked: data.abha_number_masked ?? null,
      abhaAddress:      data.abha_address       ?? null,
      verifiedAt:       data.verified_at        ?? null,
      detailsAvailable: data.details_available,
    };
  } catch (err) {
    throw _buildError(err);
  }
}


// ── HIP — M2.5 deep link ─────────────────────────────────────────────

const HIP_BASE = '/api/organisation/hip';

/**
 * Fetch the ABDM deep link for a care context.
 *
 * The deep link (abdm://link?...) can be rendered as a QR code or sent
 * directly to the patient so they can open their PHR app and link without
 * manual facility discovery.
 *
 * @param {string} careContextId
 * @returns {{ careContextId: string, status: string, deepLink: string }}
 */
export async function getCareContextDeepLink(careContextId) {
  try {
    const { data } = await apiClient.get(
      `${HIP_BASE}/care-context/${encodeURIComponent(careContextId)}/deep-link`
    );
    return {
      careContextId: data.care_context_id,
      status:        data.status,
      deepLink:      data.deep_link,
    };
  } catch (err) {
    throw _buildError(err);
  }
}


// ── HIL — M2 HIP-Initiated Linking (demographic-auth) ────────────────

/**
 * Kick off HIP-Initiated Linking for a patient. The backend resolves ABHA
 * identity, demographics, and (when care contexts aren't explicitly
 * supplied) the patient's existing care contexts entirely server-side —
 * this call never carries raw ABHA numbers or ABDM protocol details.
 *
 * On a 422 with code 'DEMOGRAPHICS_INCOMPLETE', `err.missingFields` lists
 * which patient fields need to be filled in before HIL can proceed.
 *
 * @param {{ patientId: string }} params
 * @returns {{ status: 'INITIATED'|'TOKEN_ISSUED', reused: boolean }}
 */
export async function initiateHilLinking({ patientId }) {
  try {
    const { data } = await apiClient.post(`${HIP_BASE}/care-context/link/hil-initiate`, {
      patient_id: patientId,
    });
    return { status: data.status, reused: Boolean(data.reused) };
  } catch (err) {
    throw _buildError(err);
  }
}

/**
 * Current HIL status for a patient — poll this after initiateHilLinking()
 * until it reaches a terminal state (LINKED or FAILED).
 *
 * @param {{ patientId: string }} params
 * @returns {{ status: 'NOT_STARTED'|'INITIATED'|'TOKEN_ISSUED'|'LINKED'|'FAILED' }}
 */
export async function getHilStatus({ patientId }) {
  try {
    const { data } = await apiClient.get(`${HIP_BASE}/care-context/link/hil-status`, {
      params: { patient_id: patientId },
    });
    return { status: data.status };
  } catch (err) {
    throw _buildError(err);
  }
}


// ── Health / observability ────────────────────────────────────────────

/**
 * Check current ABDM circuit breaker state.
 * Never triggers an active ABDM ping — safe to call from UI.
 *
 * @returns {{ abdmEnv: string, breakers: object, degraded: boolean }}
 */
export async function checkABHAHealth() {
  try {
    const { data } = await apiClient.get(`${BASE}/health`);
    const breakers = data.breakers ?? {};
    const degraded = Object.values(breakers).some((s) => s !== 'CLOSED');
    return { abdmEnv: data.abdm_env, breakers, degraded };
  } catch (err) {
    // Health check failure itself is not fatal — degrade silently
    return { abdmEnv: 'unknown', breakers: {}, degraded: true };
  }
}

// ABHA-address suffix for the ABDM environment (sandbox "@sbx", production "@abdm").
// Single-flight + cached: one health call per page load. A failed lookup is not cached.
let _suffixPromise = null;
export function getAbhaAddressSuffix() {
  if (!_suffixPromise) {
    _suffixPromise = checkABHAHealth().then(({ abdmEnv }) => {
      if (abdmEnv === 'unknown') _suffixPromise = null;
      return abdmEnv === 'sandbox' ? '@sbx' : '@abdm';
    });
  }
  return _suffixPromise;
}
